import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { Image } from '@outdoor-voices/ui';
import cc from 'classcat';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { KEY_CODES, KEY_DOWN } from '@outdoor-voices/ui/dist/config/constants';
import css from './ButtonsSwatches.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { jsxs as _jsxs } from "react/jsx-runtime";

var Swatch = function Swatch(_ref) {
  var currentSize = _ref.currentSize,
      disabled = _ref.disabled,
      enableClickWhenDisabled = _ref.enableClickWhenDisabled,
      image = _ref.image,
      label = _ref.label,
      on = _ref.on,
      onBlur = _ref.onBlur,
      onClick = _ref.onClick,
      onFocus = _ref.onFocus,
      sizesAvailable = _ref.sizesAvailable,
      sizeSelected = _ref.sizeSelected,
      tabIndex = _ref.tabIndex;
  var sizeSelectedUnavailable = sizeSelected && !sizesAvailable.includes(currentSize);
  var klass = cc([css.swatch, _defineProperty({}, css.disabled, disabled || sizeSelectedUnavailable), _defineProperty({}, css.on, on)]);
  var click = disabled && !enableClickWhenDisabled ? null : onClick;
  var props = {
    'aria-label': "Select the color ".concat(label),
    'aria-selected': on,
    className: klass,
    disabled: disabled && !enableClickWhenDisabled,
    onBlur: onBlur,
    onClick: click,
    onFocus: onFocus,
    role: 'option',
    type: 'button'
  }; // Only allow tabIndex of -1

  if (tabIndex && parseInt(tabIndex, 10) === -1) {
    props['tabIndex'] = tabIndex;
  }

  return /*#__PURE__*/_jsx("button", _objectSpread(_objectSpread({}, props), {}, {
    children: /*#__PURE__*/_jsx("div", {
      className: css.image,
      children: /*#__PURE__*/_jsx(Image, _objectSpread(_objectSpread({}, image), {}, {
        alt: "An image representing the product color ".concat(label),
        sizes: [56],
        wrap: false
      }))
    })
  }));
};

Swatch.defaultProps = {
  currentSize: '',
  disabled: false,
  enableClickWhenDisabled: false,
  on: false,
  sizesAvailable: [],
  sizeSelected: false,
  tabIndex: 0
};
Swatch.propTypes = {
  currentSize: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  enableClickWhenDisabled: PropTypes.bool,
  image: PropTypes.shape().isRequired,
  label: PropTypes.string.isRequired,
  on: PropTypes.bool,
  onBlur: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  sizesAvailable: PropTypes.array.isRequired,
  sizeSelected: PropTypes.bool.isRequired,
  tabIndex: PropTypes.number
};
var MAX_BUTTONS = 5;
/* eslint-disable react/no-array-index-key */

var ButtonsSwatches = function ButtonsSwatches(_ref4) {
  var currentSize = _ref4.currentSize,
      enableClickWhenDisabled = _ref4.enableClickWhenDisabled,
      expand = _ref4.expand,
      _onClick = _ref4.onClick,
      label = _ref4.label,
      options = _ref4.options,
      productTitle = _ref4.productTitle,
      sizeSelected = _ref4.sizeSelected,
      tabIndex = _ref4.tabIndex,
      value = _ref4.value;

  var _useState = useState(expand),
      _useState2 = _slicedToArray(_useState, 2),
      expanded = _useState2[0],
      setExpanded = _useState2[1];

  var _useState3 = useState(MAX_BUTTONS),
      _useState4 = _slicedToArray(_useState3, 2),
      max = _useState4[0],
      setMax = _useState4[1];

  var moreText = "+ ".concat(options.length - max, " more");
  var moreTitle = productTitle ? " For ".concat(productTitle) : '';
  var showMore = !expanded && options.length > max + 1;
  var visibleOptions = showMore ? _toConsumableArray(options.slice(0, max)) : options;
  var rootRef = useRef(null);
  var blur = useRef(null);
  var focus = useRef(null);
  useEffect(function () {
    var _rootRef$current;

    // Dynamically set the max showing based on the width of the parent element.
    var swatchWidth = 52;
    var moreButtonWidth = 64;
    var wishlistButtonWidth = 19;
    var availableSpace = Math.floor((((_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 ? void 0 : _rootRef$current.parentElement.offsetWidth) - moreButtonWidth - wishlistButtonWidth) / swatchWidth); // Always show at least 1 and no more than 5

    setMax(Math.max(1, Math.min(MAX_BUTTONS, availableSpace)));
  }, []);

  var focusOption = function focusOption(dir) {
    var lis = rootRef.current.querySelectorAll('button');
    var items = Array.from(lis);
    var index = Math.max(0, Math.min(items.indexOf(document.activeElement) + dir, items.length - 1));
    lis[index].focus();
  };

  var onKeyDown = useCallback(function (event) {
    switch (event.key) {
      case KEY_CODES.ARROW_LEFT:
      case KEY_CODES.ARROW_UP:
        event.preventDefault();
        focusOption(-1);
        break;

      case KEY_CODES.ARROW_DOWN:
      case KEY_CODES.ARROW_RIGHT:
        event.preventDefault();
        focusOption(1);
        break;

      default:
        break;
    }
  }, []);

  blur.current = function () {
    document.removeEventListener(KEY_DOWN, onKeyDown);
  };

  focus.current = function () {
    document.removeEventListener(KEY_DOWN, onKeyDown);
    document.addEventListener(KEY_DOWN, onKeyDown);
  };

  useEffect(function () {
    return function () {
      document.removeEventListener(KEY_DOWN, onKeyDown);
    };
  }, [onKeyDown]);
  return /*#__PURE__*/_jsxs("div", {
    "aria-label": label,
    className: css.root,
    ref: rootRef,
    role: "listbox",
    children: [visibleOptions.map(function (option) {
      return /*#__PURE__*/_createElement(Swatch, _objectSpread(_objectSpread({}, option), {}, {
        currentSize: currentSize,
        enableClickWhenDisabled: enableClickWhenDisabled,
        key: option.value,
        on: value === option.value,
        onBlur: blur.current,
        onFocus: focus.current,
        onClick: function onClick() {
          return _onClick(option.value);
        },
        role: "option",
        sizeSelected: sizeSelected,
        tabIndex: tabIndex
      }));
    }), showMore && /*#__PURE__*/_jsx("button", {
      "aria-label": "Click to show all color options".concat(moreTitle),
      onBlur: blur.current,
      onFocus: focus.current,
      className: css.more,
      onClick: function onClick() {
        setExpanded(true);
      },
      tabIndex: tabIndex,
      type: "button",
      children: moreText
    })]
  });
};

ButtonsSwatches.defaultProps = {
  currentSize: '',
  enableClickWhenDisabled: false,
  expand: false,
  productTitle: false,
  sizeSelected: false,
  tabIndex: 0,
  value: null
};
ButtonsSwatches.propTypes = {
  currentSize: PropTypes.string,
  enableClickWhenDisabled: PropTypes.bool,
  expand: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  productTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  sizeSelected: PropTypes.bool,
  tabIndex: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.number, PropTypes.string])
};
export default /*#__PURE__*/memo(ButtonsSwatches);